import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";

interface RevertPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paidAmount: string | number | null | undefined;
  paidViaStripe: boolean;
  isPending: boolean;
  onConfirm: (args: { reason: string }) => void;
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${Math.abs(num).toFixed(2)}`;
}

export default function RevertPaymentDialog({
  open,
  onOpenChange,
  paidAmount,
  paidViaStripe,
  isPending,
  onConfirm,
}: RevertPaymentDialogProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const canSubmit = reason.trim().length > 0 && !isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revert Payment</AlertDialogTitle>
          <AlertDialogDescription>
            Un-mark this invoice as paid. The status goes back to <strong>sent</strong> so you can edit it. The underlying financial records and unit costs that were closed by this invoice will be re-opened as outstanding.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {paidViaStripe && (
          <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-amber-800 dark:text-amber-200">
              This invoice was paid via Stripe. Reverting only changes the state in Staio — the money is still in Stripe. Issue the actual refund from the Stripe Express dashboard if applicable.
            </div>
          </div>
        )}

        <div className="space-y-3 py-2">
          <div className="text-sm text-muted-foreground">
            Amount being reverted: <span className="font-medium text-foreground">{formatMoney(paidAmount)}</span>
          </div>
          <div>
            <Label htmlFor="revert-reason">Reason</Label>
            <Textarea
              id="revert-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this payment being reverted? (e.g. wrong amount, marked paid by mistake, needs editing)"
              data-testid="input-revert-reason"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-cancel-revert">Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              if (canSubmit) onConfirm({ reason: reason.trim() });
            }}
            disabled={!canSubmit}
            data-testid="button-confirm-revert"
          >
            {isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <RotateCcw className="h-4 w-4 mr-2" />}
            Confirm Revert
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
